import { ToggleButton } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import useHasFeature from "@/features/useHasFeature";
import { SHOW_LESSON_PLAN_ASSESSMENT_QUESTIONS } from "../LessonPlannerTool.constants";
import {
  legendCss,
  toggleButtonCss,
  toggleLabelCss,
  wrapperCss,
} from "./SupportingMaterials.style";

const ToggleButtonWithTrackEvent = withTrackEvent(ToggleButton);

interface SupportingMaterialsState {
  includeProjectIdea: boolean;
  includeMultilingualLearners: boolean;
  includeAssessmentQuestions: boolean;
}

interface SupportingMaterialsProps {
  value: SupportingMaterialsState;
  onClick: (value: SupportingMaterialsState) => void;
}

const SupportingMaterials = ({ value, onClick }: SupportingMaterialsProps) => {
  const [showAssessmentQuestions] = useHasFeature(
    SHOW_LESSON_PLAN_ASSESSMENT_QUESTIONS,
  );

  return (
    <fieldset css={wrapperCss}>
      <legend css={legendCss}>
        Supporting Materials <span>(optional)</span>
      </legend>
      <ToggleButtonWithTrackEvent
        __cssFor={{
          Button: {
            root: toggleButtonCss,
          },
          label: toggleLabelCss(!!value.includeProjectIdea),
        }}
        dataProps={{
          "data-qa-selector": "lesson-planner-toggle-project-idea",
        }}
        label="Include project idea"
        onClick={() =>
          onClick({
            ...value,
            includeProjectIdea: !value.includeProjectIdea,
          })
        }
        defaultPressed={value.includeProjectIdea}
        objectType={objectTypes.generatedTextAsset}
        actionName={
          value.includeProjectIdea ? actionNames.delete : actionNames.add
        }
        streamProperties={{
          description: `Toggle ${value.includeProjectIdea ? "off" : "on"} inclusion of project idea in lesson plan`,
        }}
      />
      <ToggleButtonWithTrackEvent
        __cssFor={{
          Button: {
            root: toggleButtonCss,
          },
          label: toggleLabelCss(!!value.includeMultilingualLearners),
        }}
        dataProps={{
          "data-qa-selector": "lesson-planner-toggle-multilingual-learners",
        }}
        label="Include supports for Multilingual Learners"
        onClick={() =>
          onClick({
            ...value,
            includeMultilingualLearners: !value.includeMultilingualLearners,
          })
        }
        defaultPressed={value.includeMultilingualLearners}
        objectType={objectTypes.generatedTextAsset}
        actionName={
          value.includeMultilingualLearners
            ? actionNames.delete
            : actionNames.add
        }
        streamProperties={{
          description: `Toggle ${value.includeMultilingualLearners ? "off" : "on"} inclusion of multilingual learner supports in lesson plan`,
        }}
      />
      {showAssessmentQuestions && (
        <ToggleButtonWithTrackEvent
          __cssFor={{
            Button: {
              root: toggleButtonCss,
            },
            label: toggleLabelCss(!!value.includeAssessmentQuestions),
          }}
          dataProps={{
            "data-qa-selector": "lesson-planner-toggle-assessment-questions",
          }}
          label="Include Additional Assessment Questions"
          onClick={() =>
            onClick({
              ...value,
              includeAssessmentQuestions: !value.includeAssessmentQuestions,
            })
          }
          defaultPressed={value.includeAssessmentQuestions}
          objectType={objectTypes.generatedTextAsset}
          actionName={
            value.includeAssessmentQuestions
              ? actionNames.delete
              : actionNames.add
          }
          streamProperties={{
            description: `Toggle ${value.includeAssessmentQuestions ? "off" : "on"} inclusion of additional assessment questions in lesson plan`,
          }}
        />
      )}
    </fieldset>
  );
};

export default SupportingMaterials;
